import { experiment } from '@amplitude/unified';
import { taggedLogger } from '@/util/taggedLogger';
import type {
    AmplitudeExperimentPort,
    AmplitudeVariantSnapshot,
} from '@/stores/featureToggles/amplitudeFeatures.types';

const log = taggedLogger('amplitude-experiment-client');

type ExperimentClient = ReturnType<typeof experiment>;

/**
 * The only module in the store and domain layers that touches the analytics vendor SDK — it
 * adapts the experiment client onto the narrow port the feature-toggle store depends on.
 *
 * Returns `undefined`, never throws, when there is no client to adapt: before the SDK's
 * `initAll` has completed, or when the experiment plugin is absent. Callers treat that as
 * "no attempt possible" rather than a failed attempt.
 */
export function resolveAmplitudeExperimentPort(): AmplitudeExperimentPort | undefined {
    const client = _resolveClient();
    if (client === undefined) return undefined;

    return {
        start: async (): Promise<void> => {
            await client.start();
        },
        fetch: async (): Promise<void> => {
            await client.fetch();
        },
        variant: (key: string): AmplitudeVariantSnapshot => client.variant(key),
    };
}

/**
 * The SDK returns `undefined` rather than throwing when it has no client, but the port is a
 * seam over a beta dependency — the guard is deliberately defensive, not dead code.
 */
function _resolveClient(): ExperimentClient | undefined {
    try {
        return experiment();
    } catch (error) {
        log.error('Experiment client lookup failed; feature toggles cannot be resolved', error);
        return undefined;
    }
}
